import React from "react";
import styled from "@emotion/styled";
import { Link } from "gatsby";
import { breakpoints, theme } from "../../utils/styles";
import { Global } from "@emotion/core";
import { Helmet } from "react-helmet";
import EITFANImage from "../../assets/eit-fan-2026-paris-cohort.png";

const Page = styled.main({
  minHeight: '100vh',
  background: theme.white,
  color: theme.black,
  margin: 0,
  padding: 0,
  position: 'relative',
});

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 120px 32px 64px;

  @media (max-width: ${breakpoints.l}px) {
    padding: 100px 24px 48px;
  }
`;

const Header = styled.header`
  margin-bottom: 48px;
  text-align: left;
`;

const Title = styled.h1`
  font-size: clamp(28px, 3.5vw, 40px);
  line-height: 1.2;
  margin: 0 0 16px 0;
  color: ${theme.black};
  font-family: Nunito, sans-serif;
  font-weight: 700;
`;

const Meta = styled.div`
  font-size: 1rem;
  color: rgba(0,0,0,0.6);
  font-family: Nunito, sans-serif;
  margin-bottom: 32px;
`;

const FeaturedImage = styled.img`
  width: 100%;
  height: 400px;
  object-fit: contain;
  border-radius: 8px;
  margin-bottom: 48px;
  background: #f5f5f5;

  @media (max-width: ${breakpoints.l}px) {
    height: 300px;
    margin-bottom: 32px;
  }
`;

const Content = styled.div`
  font-family: Nunito, sans-serif;
  line-height: 1.7;
  font-size: 1.1rem;
  color: ${theme.black};

  p {
    margin: 0 0 24px 0;
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 48px 0 24px 0;
    color: ${theme.black};
  }

  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    margin: 32px 0 16px 0;
    color: ${theme.black};
  }

  blockquote {
    border-left: 4px solid ${theme.primary};
    margin: 32px 0;
    padding: 16px 24px;
    background: #f9f9f9;
    font-style: italic;
  }

  ul, ol {
    margin: 24px 0;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }

  .highlight {
    background: linear-gradient(120deg, ${theme.primary}20 0%, ${theme.primary}20 100%);
    padding: 2px 4px;
    border-radius: 3px;
  }

  a {
    color: ${theme.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${theme.primary};
  text-decoration: none;
  font-family: Nunito, sans-serif;
  font-weight: 600;
  margin-bottom: 32px;

  &:hover {
    text-decoration: underline;
  }

  &:before {
    content: "←";
    font-size: 1.2rem;
  }
`;

export default function EITFoodFAN2026Post() {
  return (
    <Page>
      <Helmet>
        <title>Scindo Joins the EIT Food Accelerator Network 2026, Paris Hub | Scindo</title>
        <meta name="description" content="Scindo has been selected to join the EIT Food Accelerator Network (FAN) 2026, participating in the Paris Hub cohort focused on Biotech Ingredients, Products and Processes." />

        {/* Open Graph tags for social media sharing */}
        <meta property="og:title" content="Scindo Joins the EIT Food Accelerator Network 2026, Paris Hub" />
        <meta property="og:description" content="Scindo has been selected to join the EIT Food Accelerator Network (FAN) 2026, participating in the Paris Hub cohort focused on Biotech Ingredients, Products and Processes." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.scindo.bio/blog/eit-food-fan-2026-paris-hub" />
        <meta property="og:site_name" content="Scindo" />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Scindo Joins the EIT Food Accelerator Network 2026, Paris Hub" />
        <meta name="twitter:description" content="Scindo has been selected to join the EIT Food Accelerator Network (FAN) 2026, Paris Hub cohort." />

        {/* Additional SEO tags */}
        <meta name="author" content="Scindo" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.scindo.bio/blog/eit-food-fan-2026-paris-hub" />
      </Helmet>

      <Global styles={{
        'html, body': {
          background: theme.white,
          color: theme.black,
          margin: 0,
          padding: 0,
        },
        '*': {
          boxSizing: 'border-box',
        },
      }} />

      <Container>
        <BackLink to="/media">Back to Media</BackLink>

        <Header>
          <Title>Scindo Joins the EIT Food Accelerator Network 2026, Paris Hub</Title>
          <Meta>1 July 2026</Meta>
        </Header>

        <FeaturedImage
          src={EITFANImage}
          alt="Scindo among the Paris Hub startup cohort of the EIT Food Accelerator Network 2026"
        />

        <Content>
          <p>
            Scindo has been selected to join the <span className="highlight">EIT Food Accelerator Network (FAN) 2026</span>, one of Europe's leading agrifood and foodtech accelerator programmes. Scindo will participate in the Paris Hub cohort, focused on Biotech Ingredients, Products and Processes, delivered in partnership with Shakeup Factory.
          </p>

          <p>
            The programme runs from September to December 2026 and brings together a selected cohort of high-potential startups from across Europe. Over the course of the accelerator, participating companies work on technology validation, go-to-market acceleration, and investment readiness, with direct access to the EIT Food innovation community, European research facilities, and industry partners.
          </p>

          <h2>Accelerating Scindo's Natural Flavour Platform</h2>

          <p>
            Scindo has joined the programme to accelerate the commercialisation of its natural flavour platform. Powered by AI-designed enzymes, the platform converts clean, natural feedstocks into natural flavour ingredients, offering a direct replacement for synthetic, petrochemically derived alternatives.
          </p>

          <p>
            Scindo's cell-free enzymatic process produces natural lactone flavours, the compounds behind creamy and fruity profiles spanning coconut, butter, strawberry and peach, at lower cost, energy and complexity than conventional production routes.
          </p>

          <h2>Validating Technology with Industry Partners</h2>

          <p>
            Participation in EIT FAN provides Scindo with an ideal environment to accelerate its commercialisation timelines with potential customers and collaborators. The Paris Hub's focus on biotech ingredients and bioprocessing aligns well with Scindo's technology and its stage of development, positioning the company to move from validated technology towards commercial launch.
          </p>

          <p>
            Scindo looks forward to working with the EIT Food team and the wider 2026 cohort, an exciting and innovative group of companies building the future of the European food system.
          </p>

          <p>
            🔗 More information:
          </p>
          <ul>
            <li><a href="https://www.eitfood.eu/news/eit-food-selects-65-startups-for-2026-fan-programme-across-six-specialised-technology-hubs-to-accelerate-europes-innovation" target="_blank" rel="noopener noreferrer">EIT Food selects 65 startups for 2026 FAN programme across six specialised technology hubs</a></li>
            <li><a href="https://eitfoodentrepreneurship.turtl.co/story/fan-cohort-2026/page/1" target="_blank" rel="noopener noreferrer">FAN Cohort 2026 story</a></li>
          </ul>
        </Content>
      </Container>
    </Page>
  );
}
